import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { isEmpty, isNil, sortByKey } from '@semios/app-platform-common'
import type { TAggregationRequestedValue } from '@semios/app-platform-value-type-definitions'
import { FIRE_BLIGHT_DEFAULT, riskIdColorPicker } from '@semios/app-platform-values-helpers-and-types'
import { propertyLacksPermissionSectionMaker } from 'App/Map/PanelDetails/_utils/propertyLacksPermissionSectionMaker'
import { selectedPropertyHasPermission } from 'App/Map/PanelDetails/_utils/selectedPropertyHasPermission'
import { DropdownSelectorProperty } from 'App/Map/PanelDetails/SectionTitleBars/DropdownSelectorProperty/DropdownSelectorProperty'
import { FireBlightSettingsTitleLink } from 'App/Map/PanelDetails/SectionTitleBars/FireBlightSettingsTitleLink/FireBlightSettingsTitleLink'
import { TitleHelpIconTooltip } from 'App/Map/PanelDetails/SectionTitleBars/TitleHelpIconTooltip/TitleHelpIconTooltip'
import { BlockWithWeatherStationLabel } from 'App/Map/PanelDetails/SummaryGridTable/BlockWithWeatherStationLabel/BlockWithWeatherStationLabel'
import type { TValuesTimeseriesProperties } from 'App/Map/types'
import type { GridTableContentSection } from 'components/GridTable/types'
import { translate } from 'i18n/i18n'
import { keyBy } from 'lodash'
import { colors } from 'settings/colors'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TSelectedFieldAssetsStoreState } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import type { selectedValueGroupsStore } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import { filterFieldAssetsByValueTypes } from 'utils/filterFieldAssetsByValueTypes'
import { getPointExternalVendorNames } from 'utils/getPointExternalVendorNames'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import { BadgeCell } from '../../../BadgeCell/BadgeCell'

const permission = 'VIEW_FIRE_BLIGHT_DETAILS'

const preferredAggregationInterval = {
  preferredAggregationInterval: 'DAILY',
} satisfies TAggregationRequestedValue

const checkPermission = () => selectedPropertyHasPermission({ permission })
const valueType = 'fireBlightTRV'

export const apiArgs = ({
  selectedValueGroups,
  selectedFieldAssets,
}: {
  selectedValueGroups: ReturnType<typeof selectedValueGroupsStore.getState>['selectedValueGroups']
  selectedFieldAssets: TSelectedFieldAssetsStoreState
}): Partial<routes.Values.Request> => {
  if (!checkPermission()) return {}

  if (!selectedValueGroups.fire_blight) return {}

  if (!selectedFieldAssets.property) return {}

  const blocks = fieldAssetStore.getState()?.properties?.[selectedFieldAssets.property]?.blocks ?? {}

  const relevantBlocks = filterFieldAssetsByValueTypes({
    fieldAssets: Object.values(blocks),
    valuesTimeseries: [valueType],
  })

  if (isEmpty(relevantBlocks)) return {}

  return {
    blocks: {
      blockIds: relevantBlocks.map((b) => b.blockId),
      valuesRequested: {
        [valueType]: preferredAggregationInterval,
      },
    },
  }
}

export const content = ({
  data,
}: {
  data: routes.Values.Response
  updateData: (pathToSet: string, dataToSet: unknown) => void
}): GridTableContentSection => {
  const propertyId = selectedFieldAssetsStore.getState()?.property
  const blocks = fieldAssetStore.getState()?.properties?.[Number(propertyId)]?.blocks ?? {}
  const points = fieldAssetStore.getState()?.properties?.[Number(propertyId)]?.points ?? {}

  const relevantBlocks = filterFieldAssetsByValueTypes({
    fieldAssets: Object.values(blocks),
    valuesTimeseries: [valueType],
  })
    .map((block) => ({ ...block, name: block.name ?? translate.phrases.banyanApp('Unnamed Block') }))
    .sort(sortByKey('name'))

  const commonReturnItems = {
    id: 'fireBlight',
    title: (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span>{unitConverter.fireBlightDaily().categoryTitleWithoutUnit()}</span>
        <TitleHelpIconTooltip>{unitConverter.fireBlightDaily().title()}</TitleHelpIconTooltip>
      </div>
    ),
    titleCss: { width: '100%' },
    titleLink: FireBlightSettingsTitleLink({
      valuesTimeseries: [valueType],
      riskId: FIRE_BLIGHT_DEFAULT,
    }),
    fieldAssetPicker: (
      <DropdownSelectorProperty
        valuesTimeseriesToFilterOn={[valueType as TValuesTimeseriesProperties]}
        propertyPermissionsToCheck={[permission]}
      />
    ),
  }

  if (!checkPermission() || isEmpty(relevantBlocks))
    return propertyLacksPermissionSectionMaker(commonReturnItems)

  type PossibleValuesRecord = { timestamp: string; value: number | null | undefined }

  const items = relevantBlocks.map((block) => {
    const blockData = data?.blocks?.[block.blockId]?.values?.fireBlightTRV?.[0]
    const blockName = block.name

    const weatherStationLabel = block.associatedWeatherStation
      ? (points[block.associatedWeatherStation]?.name ?? '')
      : ''

    const weatherStationExternalVendorNames = block.associatedWeatherStation
      ? getPointExternalVendorNames({ point: points[block.associatedWeatherStation], deviceKind: 'weather' })
      : undefined

    const values = keyBy(
      blockData?.timeseries?.map(({ timestamp, value }) => ({ timestamp, value })) ?? [],
      'timestamp',
    )

    const riskId = blockData?.metadata?.riskId ?? FIRE_BLIGHT_DEFAULT
    const riskColor = riskIdColorPicker[riskId]

    return {
      id: `fire_blight_${block.blockId}`,
      height: 70,
      label: (
        <BlockWithWeatherStationLabel
          label={blockName}
          weatherStationLabel={weatherStationLabel}
          weatherStationExternalVendorNames={weatherStationExternalVendorNames}
        />
      ),
      labelMinWidth: 160,
      valueMinWidth: 120,
      values,
      render: (value: PossibleValuesRecord) => {
        const riskValue = value?.value

        if (isNil(riskValue)) {
          return translate.phrases.templates('-')
        }

        return (
          <BadgeCell backgroundColor={riskColor(riskValue) ?? colors.white}>
            {unitConverter.fireBlightDaily(riskValue).value()}
          </BadgeCell>
        )
      },
    }
  })

  return {
    ...commonReturnItems,
    items,
  }
}
